import {ToolbarAction} from "../../../../shared/secondary-toolbar/secondary-toolbar.component";
import {TaskRecordStateMachine} from "../../../../lib/task/task-record-statemachine";
import {TaskRecordStateItem} from "../../../../util/task-record-utils";

export interface TaskRecordActionsConfig {
  // Data Flags
  taskRecordDefined: boolean;
  readonly: boolean;
  hasProcess: boolean;
  isSimplified?: boolean;
  settableStateList: TaskRecordStateItem[];

  // Permission Flags
  allowEdit?: boolean;
  allowInvoice?: boolean;
  allowClone?: boolean;
  allowBulkClone?: boolean;
  allowAssigneeChangeInProgress?: boolean;
  allowDeadlineChange?: boolean;
  allowAgreedTimeChange?: boolean;
  allowApprovalStart?: boolean;
  allowApprovalCancel?: boolean;
  allowDownloadAllFiles?: boolean;
  allowGeocode?: boolean;
  sendMessage?: boolean;

  // Click Handlers (Callbacks)
  onInvoice: () => void;
  onClone: () => void;
  onDownloadAllFiles: () => void;
  onChangeAgreedTime: () => void;
  onStartApproval: () => void;
  onCancelApproval: () => void;
  onChangeState: (stateId: TaskRecordStateMachine.State) => void;
  onFinishAndJump: (state: TaskRecordStateMachine.State) => void;
  onEdit: () => void;
  onChangeDeadline: () => void;
  onChangeAssigneeInProgress: () => void;
  onMessage: () => void;
  onGeocode: () => void;
  onBulkClone: () => void;
}

export class TaskRecordActionsUtil {

  static buildActions(config: TaskRecordActionsConfig): ToolbarAction[] {
    const actions: ToolbarAction[] = [];
    const stateList: TaskRecordStateItem[] = config.settableStateList || [];

    // Internal business rule matchers
    const finishAndJumpAllowed = config.hasProcess && stateList.some(s => s.stateObject?.state === 'FINISHED');
    const newFinishAndJumpAllowed = config.hasProcess && stateList.some(s => s.stateObject?.state === 'NEW_FINISHED');

    // 1. Create Invoice
    if (config.allowInvoice) {
      actions.push({
        titleStringKey: 'TASK_RECORD_CREATE_INVOICE',
        iconClass: 'icomoon-sidebar-tasks',
        action: config.onInvoice,
        priority: 40
      });
    }

    // 2. Clone
    if (config.taskRecordDefined && config.allowClone) {
      actions.push({
        titleStringKey: 'TASK_RECORD_CLONE_TITLE',
        iconClass: 'icomoon-clone',
        action: config.onClone,
        priority: 60
      });
    }

    // 3. Download All Files
    if (config.taskRecordDefined && config.allowDownloadAllFiles) {
      actions.push({
        titleStringKey: 'COMMON_DOWNLOAD_ALL_FILES',
        iconClass: 'icomoon-download',
        action: config.onDownloadAllFiles,
        priority: 40
      });
    }

    // 4. Change Agreed Time
    if (config.taskRecordDefined && config.allowAgreedTimeChange) {
      actions.push({
        titleStringKey: 'TASK_RECORD_CHANGE_AGREED_TIME',
        iconClass: 'icomoon-deadline',
        action: config.onChangeAgreedTime,
        priority: 30
      });
    }

    // 5. Start Approval
    if (config.taskRecordDefined && config.allowApprovalStart) {
      actions.push({
        titleStringKey: 'TASK_RECORD_START_APPROVAL',
        iconClass: 'icomoon-new-state-pending-approval',
        action: config.onStartApproval,
        priority: 80
      });
    }

    // 6. Cancel Approval
    if (config.taskRecordDefined && config.allowApprovalCancel) {
      actions.push({
        titleStringKey: 'TASK_RECORD_CANCEL_APPROVAL',
        iconClass: 'icomoon-restart',
        action: config.onCancelApproval,
        priority: 80
      });
    }

    // 7. State Changes (Forced Simplified handling)
    if (stateList.length > 0) {
      stateList.forEach(state => {
        actions.push({
          titleStringKey: state.text,
          iconClass: state.stateObject?.iconEditClass || 'icomoon-modify',
          action: () => config.onChangeState(state.id!),
          priority: 90
        });
        // 8. Finish and Jump
        if (finishAndJumpAllowed && state.id === 'FINISHED') {
          actions.push({
            titleStringKey: 'TASK_RECORD_STATE_CHANGE_FINISH_AND_JUMP',
            iconClass: 'icomoon-state-action-validate',
            action: () => config.onFinishAndJump('FINISHED'),
            priority: 90
          });
        }
        // 9. New Finish and Jump
        if (newFinishAndJumpAllowed && state.id === 'NEW_FINISHED') {
          actions.push({
            titleStringKey: 'TASK_RECORD_STATE_CHANGE_FINISH_AND_JUMP',
            iconClass: 'icomoon-state-action-finish',
            action: () => config.onFinishAndJump('NEW_FINISHED'),
            priority: 90
          });
        }
      });
    }


    // --- FORMER DROPDOWN ITEMS ---
    if (config.taskRecordDefined) {

      // 11. Deadline Modification
      if (config.allowDeadlineChange) {
        actions.push({
          titleStringKey: 'COMMON_BUTTON_DEADLINE_MODIFICATION',
          iconClass: 'icomoon-deadline',
          action: config.onChangeDeadline,
          priority: 30
        });
      }

      // 12. Assignee Modification
      if (config.allowAssigneeChangeInProgress) {
        actions.push({
          titleStringKey: 'COMMON_BUTTON_ASSIGNEE_MODIFICATION',
          iconClass: 'icomoon-deadline-alt',
          action: config.onChangeAssigneeInProgress,
          priority: 30
        });
      }

      // 13. Send New Message
      if (config.sendMessage) {
        actions.push({
          titleStringKey: 'TASK_RECORD_EDIT_SEND_NEW_MESSAGE',
          iconClass: 'icomoon-chat-message',
          action: config.onMessage,
          priority: 10
        });
      }

      // 14. Geocode
      if (config.allowGeocode && config.readonly) {
        actions.push({
          titleStringKey: 'TASK_GEOCODING_TITLE',
          iconClass: 'icomoon-location',
          action: config.onGeocode,
          priority: 10
        });
      }

      // 15. Bulk Clone
      if (config.allowBulkClone) {
        actions.push({
          titleStringKey: 'TASK_RECORD_BULK_CLONE_TITLE',
          iconClass: 'icomoon-clone',
          action: config.onBulkClone,
          priority: 20
        });
      }

      // 10. Edit Button
      if (config.taskRecordDefined && config.allowEdit) {
        actions.push({
          titleStringKey: 'COMMON_BUTTON_EDIT',
          iconClass: 'icomoon-modify',
          action: config.onEdit,
          priority: 100
        });
      }

    }

    return actions;
  }
}
